import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { HeartIcon, ShareIcon, ChevronLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Loader from '../../components/Loader';
import ErrorMessage from '../../components/ErrorMessage';
import { getProductDetailsById } from '../../services/user';

interface Product {
  _id: string;
  productName: string;
  price: number;
  maxPrice?: number;
  quantity: number;
  productDescription: string;
  featureHighlights?: string;
  additionalHighlights?: string;
  productImage: string[];
  backImage?: string;
  isVegetarian: boolean;
  brandOwnerFSSAILicenseNo?: string;
  importerFSSAILicenseNo?: string;
  isCancellable: boolean;
  availableOnCod: boolean;
  fssaiCertificate?: string;
  GST_Percentage?: number;
  productSubcategory: string;
  UOM?: string;
  UOMvalue?: string;
  manufacturedDate?: string;
  nutritionalInfo?: string;
  manufacturerOrPackerName?: string;
  manufacturerOrPackerAddress?: string;
  maxAllowedQty?: number;
  length?: number;
  breadth?: number;
  height?: number;
  weight?: number;
  colour?: string;
  brand?: string;
  material?: string;
  size?: string;
  gender?: string;
  sizeChart?: string;
  fabric?: string;
  colourName?: string;
  strapMaterial?: string;
  waterResistant?: string;
  display?: string;
  glassMaterial?: string;
  sportType?: string;
  baseMetal?: string;
  planting?: string;
}

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    const fromPage = (location.state as any)?.fromPage;
    if (fromPage && fromPage > 1) {
      navigate(`/products?page=${fromPage}`);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/products');
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await getProductDetailsById(id);
        setProduct(response.data);
      } catch (err) {
        setError('Failed to fetch product details. Please try again later.');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const renderDetailItem = (label: string, value: any) => {
    if (!value && value !== 0 && value !== false) return null;

    return (
      <div className="py-2 border-b border-gray-100">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
        </dd>
      </div>
    );
  };

  if (loading) return <Loader />;
  if (error) return <ErrorMessage message={error} />;
  if (!product) return <ErrorMessage message="Product not found" />;

  return (
    <div className="bg-white">
      {/* Navigation */}
      <nav className="px-4 py-3 border-b border-gray-200 flex items-center">
        <button
          className="p-1 rounded-full hover:bg-gray-100"
          onClick={handleBack}
          title="Back to products"
        >
          <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
        </button>
        <h2 className="ml-2 text-lg font-medium text-gray-900">Product Details</h2>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-x-8">
          {/* Image gallery */}
          <div className="mt-6 lg:mt-0">
            <Carousel
              showThumbs={true}
              infiniteLoop={true}
              showStatus={false}
              emulateTouch={true}
              className="rounded-lg overflow-hidden"
            >
              {product.productImage.map((image, index) => (
                <div key={index} className="aspect-w-1 aspect-h-1">
                  <img
                    src={image}
                    alt={`Product ${index + 1}`}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              ))}
            </Carousel>
            {product.backImage && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-900">Back View</h3>
                <img
                  src={product.backImage}
                  alt="Product back view"
                  className="mt-2 w-full rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="lg:pl-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  {product.productName}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {product.brand && `${product.brand} • `}{product.productSubcategory}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  className="p-2 rounded-full hover:bg-gray-100 text-green-600"
                  onClick={() => {
                    const fromPage = (location.state as any)?.fromPage;
                    navigate(`/products/edit/${product._id}`, {
                      state: { fromPage }
                    });
                  }}
                  title="Edit Product"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button className="p-2 rounded-full hover:bg-gray-100">
                  <HeartIcon className="h-5 w-5 text-gray-600" />
                </button>
                <button className="p-2 rounded-full hover:bg-gray-100">
                  <ShareIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Price */}
            <div className="mt-6">
              <div className="flex items-center">
                <p className="text-3xl font-bold text-gray-900">₹{product.price}</p>
                {product.maxPrice && (
                  <>
                    <p className="ml-3 text-lg text-gray-500 line-through">₹{product.maxPrice}</p>
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {Math.round(((product.maxPrice - product.price) / product.maxPrice) * 100)}% OFF
                    </span>
                  </>
                )}
              </div>
              {(product.UOM || product.UOMvalue) && (
                <p className="mt-1 text-sm text-gray-500">
                  {product.UOMvalue} {product.UOM}
                </p>
              )}
            </div>

            {/* Availability */}
            <div className="mt-4">
              {product.quantity > 0 ? (
                <p className="flex items-center text-sm text-green-600">
                  <span className="h-2 w-2 rounded-full bg-green-600 mr-2"></span>
                  In stock {product.quantity && `(${product.quantity} available)`}
                </p>
              ) : (
                <p className="flex items-center text-sm text-red-600">
                  <span className="h-2 w-2 rounded-full bg-red-600 mr-2"></span>
                  Out of stock
                </p>
              )}
              {product.maxAllowedQty && (
                <p className="mt-2 text-sm text-gray-500">
                  Max order quantity: {product.maxAllowedQty}
                </p>
              )}
            </div>

            {/* Highlights */}
            {product.featureHighlights && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900">Highlights</h3>
                <div className="mt-4">
                  <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
                    {product.featureHighlights.split(',').map((highlight, index) => (
                      <li key={index}>{highlight.trim()}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {product.additionalHighlights && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900">Additional Highlights</h4>
                <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mt-2">
                  {product.additionalHighlights.split(',').map((highlight, index) => (
                    <li key={index}>{highlight.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Description */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-900">Description</h3>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-gray-700">{product.productDescription}</p>
              </div>
            </div>

            {/* Policies */}
            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">COD</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    {product.availableOnCod ? 'Available' : 'Not available'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product details */}
        <div className="mt-16 lg:mt-24">
          <h2 className="text-lg font-medium text-gray-900">Product Details</h2>

          <div className="mt-6 border-t border-gray-200">
            <dl className="divide-y divide-gray-200">
              {product.brand && renderDetailItem('Brand', product.brand)}
              {/* {renderDetailItem('Subcategory', product.productSubcategory)} */}
              {renderDetailItem('Vegetarian', product.isVegetarian)}
              {product.manufacturerOrPackerName && renderDetailItem('Manufacturer/Packer', product.manufacturerOrPackerName)}
              {product.manufacturerOrPackerAddress && renderDetailItem('Manufacturer Address', product.manufacturerOrPackerAddress)}
              {product.manufacturedDate && renderDetailItem('Manufactured Date', product.manufacturedDate)}
              {product.brandOwnerFSSAILicenseNo && renderDetailItem('FSSAI License (Brand Owner)', product.brandOwnerFSSAILicenseNo)}
              {product.importerFSSAILicenseNo && renderDetailItem('FSSAI License (Importer)', product.importerFSSAILicenseNo)}
              {product.GST_Percentage && renderDetailItem('GST Percentage', `${product.GST_Percentage}%`)}
              {product.nutritionalInfo && renderDetailItem('Nutritional Information', product.nutritionalInfo)}
              {product.colour && renderDetailItem('Color', product.colour)}
              {product.weight && renderDetailItem('Weight', `${product.weight} grams`)}
              {product.length && product.breadth && product.height &&
                renderDetailItem('Dimensions', `${product.length} × ${product.breadth} × ${product.height} cm`)}
              {product.material && renderDetailItem('Material', product.material)}
              {product.size && renderDetailItem('Size', product.size)}
              {product.gender && renderDetailItem('Gender', product.gender)}
              {product.fabric && renderDetailItem('Fabric', product.fabric)}
              {product.strapMaterial && renderDetailItem('Strap Material', product.strapMaterial)}
              {product.waterResistant && renderDetailItem('Water Resistant', product.waterResistant)}
              {product.display && renderDetailItem('Display', product.display)}
              {product.glassMaterial && renderDetailItem('Glass Material', product.glassMaterial)}
              {product.sportType && renderDetailItem('Sport Type', product.sportType)}
              {product.baseMetal && renderDetailItem('Base Metal', product.baseMetal)}
              {product.planting && renderDetailItem('Planting', product.planting)}
            </dl>
          </div>
        </div>

        {/* Certificates */}
        {product.fssaiCertificate && (
          <div className="mt-16 lg:mt-24">
            <h2 className="text-lg font-medium text-gray-900">Certificates</h2>
            <div className="mt-6">
              <a
                href={product.fssaiCertificate}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              >
                View FSSAI Certificate
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;